<template>
  <div v-if="type === 'radio'">
    <div v-for="(v, index) in radioValues" :key="index">
      <VField :name="name" type="radio" :value="v.value" :id="v.value" :validateOnBlur="false" :validateOnChange="true" :validateOnInput="false"/>
      <label :for="v.value" class="ml-2">{{ v.display }}</label>
    </div>
    <ErrorMessage :name="name" v-slot="{ message }">
      <span class="text-red flex items-center mt-1" v-show="message">
        <ExclamationIcon class="inline h-7 mr-3 top-0.5 relative flex-shrink-0"/>
        <span>{{ message }}</span>
      </span>
    </ErrorMessage>
  </div>

  <div v-else>
    <VField :name="name" v-slot="{ field, resetField, errorMessage }" :type="type" :value="type === 'checkbox' ? true : undefined" :unchecked-value="false" :validateOnBlur="false" :validateOnChange="true" :validateOnInput="false" :validateOnMount="false">
      <div v-if="type === 'checkbox'" class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 items-center">
        <div class="flex items-center">
          <input type="checkbox" v-bind="field" :disabled="disabled" :id="name" class="hidden" :value="true"/>
          <label :for="name" class="flex-grow flex items-start" :class="{ 'text-gray-400': disabled }">
            <div class="h-3.5 w-3.5 rounded-sm ring-gray-400 mr-2 mt-1.5 flex-shrink-0" :class="{ 'bg-white': !field.checked && !disabled, 'bg-gray-400': field.checked && !disabled, 'bg-gray-100': disabled, 'ring-1': !disabled }">
              <CheckIcon v-if="field.checked" :class="{ 'text-white': !disabled, 'text-gray-400': disabled }"/>
            </div>
            {{ label }}
            <div v-if="$slots.labelRight" class="ml-2">
              <slot name="labelRight"></slot>
            </div>
          </label>
          <div v-if="$slots.right" class="ml-2 flex items-center">
            <slot name="right"></slot>
          </div>
        </div>
        <span class="text-red flex items-center mt-1 lg:mt-0 lg:ml-3 xl:col-span-2" v-show="errorMessage">
          <ExclamationIcon class="inline h-7 mr-3 top-0.5 relative flex-shrink-0"/>
          <span>{{ errorMessage }}</span>
        </span>
      </div>     
      <div v-else class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 items-center">
        <div class="flex" :class="{ 'h-full': type === 'textarea' }">
          <div class="flex-grow ring-1 ring-gray-400 rounded-md flex flex-col pb-1 px-3" :class="{ 'text-red ring-2 ring-red': errorMessage, 'bg-gray-200': disabled }">
            <label :for="name" class="pt-1 text-gray-400 text-xs font-bold uppercase" :class="{ 'text-red': errorMessage, 'text-gray-500': !disabled }">{{ label }}</label>
            
            <div v-if="type === 'textarea'" class="h-40 lg:h-72 flex-grow" :class="{ 'text-red': errorMessage, 'text-gray-400': disabled }">
              <ckeditor :model-value="transformNull(field.value)" @input="editorInput(resetField, $event)" :disabled="disabled" :id="name" :editor="editor" :config="editorConfig" class="h-full"/>
            </div>

            <select v-else-if="type === 'select'" v-bind="field" :disabled="disabled" @change="changed(resetField, $event)" class="bg-white bg-opacity-0 -ml-1 outline-none w-full">
              <option v-if="emptyOption"></option>
              <option v-for="option in options" :key="option.value" :value="option.value">{{ option.display }}</option>
            </select>

            <input v-else :disabled="disabled" :type="type" :placeholder="placeholder || label" :id="name" :autocomplete="autocomplete" v-bind="field" @change="removeEmpty(resetField, $event)" class="w-full rounded-md outline-none bg-transparent"/>
          </div>
          <slot name="right" class="flex-shrink-0"></slot>
        </div>
        <span class="text-red flex items-center mt-1 lg:mt-0 lg:ml-3 xl:col-span-2" v-show="errorMessage">
          <ExclamationIcon class="inline h-7 mr-3 top-0.5 relative flex-shrink-0"/>
          <span>{{ errorMessage }}</span>
        </span>
      </div>
    </VField>
  </div>
</template>

<script>
import InlineEditor from '@ckeditor/ckeditor5-build-inline'
import EditorLangNl from '@ckeditor/ckeditor5-build-inline/build/translations/nl'
import EditorLangEn from '@ckeditor/ckeditor5-build-inline/build/translations/en-gb'
import EditorLangFr from '@ckeditor/ckeditor5-build-inline/build/translations/fr'
import { Field as VField, ErrorMessage } from 'vee-validate'
import { ExclamationIcon } from '@heroicons/vue/solid'
import { CheckIcon } from '@heroicons/vue/outline'

export default {
  name: 'Field',
  components: {
    VField,
    ErrorMessage,
    ExclamationIcon,
    CheckIcon
  },
  props: {
    name: String,
    label: String,
    placeholder: String,
    type: String,
    default: Object,
    autocomplete: {
      type: String,
      default: "nope" // "off" doesn't work on Chrome. This workaround disables autofill but still leaves suggestions. Not ideal. Best solution wouldn't suggest anything.
    },
    disabled: {
      type: Boolean,
      value: false
    },
    options: Array,
    emptyOption: {
      type: Boolean,
      default: false
    },
    radioValues: Array
  },
  emits: ['change'],
  data () {
    return {
      editor: InlineEditor,
      editorConfig: {
        placeholder: this.placeholder || this.label,
        toolbar: ['bold', 'italic', '|', 'bulletedList', 'numberedList', 'insertTable', '|', 'undo', 'redo'],
        language: this.$i18n.locale
      },
    }
  },
  methods: {
    transformNull (value) {
      if (value === undefined || value === null) {
        return ''
      } else {
        return value
      }
    },
    changed (reset, event) {
      if (typeof event.target.value === 'string' && event.target.value.trim() === '') {
        // If no default is provided, default is undefined, which is precisely what we would want: this input's key will be removed from the object.
        // If a value is provided, then that value is set.
        reset({ value: this.default })
        this.$emit('change', this.default)
      } else {
        this.$emit('change', event.target.value)
      }
    },
    editorInput(reset, event) {
      if (!event.target) { // For some reason two events are emitted from ckeditor @input. One that we want, the other that we don't.
        if (typeof event === 'string' && event.trim() === '') {
          // If no default is provided, default is undefined, which is precisely what we would want: this input's key will be removed from the object.
          // If a value is provided, then that value is set.
          reset({ value: this.default })
          this.$emit('change', this.default)
        } else {
          reset({ value: event })
          this.$emit('change', event)
        }
      }
    },
    removeEmpty (reset, event) {
      if (typeof event.target.value === 'string' && event.target.value.trim() === '') {
        // If no default is provided, default is undefined, which is precisely what we would want: this input's key will be removed from the object.
        // If a value is provided, then that value is set.
        reset({ value: this.default })
      }
    }
  }
}
</script>

<style scoped>
/* Remove blue background on input fields when Chrome autofills. */
input:-webkit-autofill, input:-webkit-autofill:focus {
  transition: background-color 5000s ease-in-out 0s !important;
}

.ck.ck-editor__editable.ck-focused:not(.ck-editor__nested-editable) {
  border: none;
  box-shadow: none;
}

.ck.ck-editor__editable_inline {
  border: none;
  padding: 0;
}

.ck.ck-editor__editable_inline>:first-child {
  margin-top: 0;
}

.ck.ck-editor__editable_inline>:last-child {
  margin-bottom: 0;
}
</style>